import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';

interface LineageRow {
  readonly lineageId: string;
  readonly lineageCode: string;
  readonly sourceAssetType: string;
  readonly sourceAssetRef: string;
  readonly targetAssetType: string;
  readonly targetAssetRef: string;
  readonly transformationType: string;
  readonly direction: string;
  readonly depth: number;
}

interface DatasetLineage {
  readonly upstream: readonly LineageRow[];
  readonly downstream: readonly LineageRow[];
}

const ASSET_TYPES = ['DATASET', 'ANALYTICAL_DATASET', 'MODULE_ENTITY', 'EXTERNAL', 'FORECAST', 'SCENARIO', 'INSIGHT', 'REPORT'];
const TRANSFORMATIONS = ['INGEST', 'CLEANSE', 'AGGREGATE', 'JOIN', 'DERIVE', 'ENRICH', 'FORECAST', 'COPY', 'EXPORT'];
const DIRECTIONS = ['UPSTREAM', 'DOWNSTREAM'];

/**
 * M41 — Data Lineage Viewer (§26 / §16). The lineage surface over the directed
 * lineage edges (T537). Record a lineage edge (source asset → transformation →
 * target asset) for a dataset — BR-02 mandates lineage is maintained — and view
 * a dataset's resolved upstream / downstream lineage. Viewing needs LINEAGE_VIEW,
 * recording needs LINEAGE_MANAGE.
 */
export function DataLineageViewer(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [datasetId, setDatasetId] = useState('');
  const [query, setQuery] = useState('');
  const [lineage, setLineage] = useState<DatasetLineage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [reload, setReload] = useState(0);

  const [edgeDatasetId, setEdgeDatasetId] = useState('');
  const [sourceType, setSourceType] = useState('MODULE_ENTITY');
  const [sourceRef, setSourceRef] = useState('');
  const [targetType, setTargetType] = useState('DATASET');
  const [targetRef, setTargetRef] = useState('');
  const [transformationType, setTransformationType] = useState('INGEST');
  const [direction, setDirection] = useState('UPSTREAM');
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    if (!query) { setLineage(null); return; }
    let alive = true;
    setLoading(true);
    apiRequest<{ data: DatasetLineage }>(`/v1/data-lake/lineage/dataset/${query}`, { token })
      .then((r) => { if (alive) { setLineage(r.data); setError(null); } })
      .catch((e) => { if (alive) { setLineage(null); setError((e as Error).message); } })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, query, reload]);

  const lookup = (e: FormEvent): void => {
    e.preventDefault();
    setNotice(null);
    setActionError(null);
    setQuery(datasetId.trim());
  };

  const record = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!edgeDatasetId.trim()) { setActionError(t('m41.common.datasetIdRequired')); return; }
    if (!sourceRef.trim() || !targetRef.trim()) { setActionError(t('m41.lineage.refsRequired')); return; }
    try {
      await apiRequest('/v1/data-lake/lineage', {
        method: 'POST',
        body: {
          datasetId: edgeDatasetId.trim(),
          sourceAssetType: sourceType,
          sourceAssetRef: sourceRef.trim(),
          targetAssetType: targetType,
          targetAssetRef: targetRef.trim(),
          transformationType,
          direction,
        },
        token,
      });
      setNotice(t('m41.lineage.edgeRecorded'));
      setSourceRef('');
      setTargetRef('');
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m41.lineage.notAuthRecord'));
      } else {
        setActionError((err as Error).message);
      }
    }
  };

  const renderEdges = (label: string, edges: readonly LineageRow[]): ReactNode => (
    <DataTable
      ariaLabel={`${label}-table`}
      testId={`${label}-data-table`}
      rows={edges}
      rowKey={(l) => l.lineageId}
      emptyMessage={t(`m41.lineage.empty.${label}`)}
      columns={[
        { key: 'code', header: t('m41.common.code'), cellAriaLabel: (l) => `lineage-code-${l.lineageId}`, render: (l) => l.lineageCode },
        { key: 'source', header: t('m41.common.source'), render: (l) => <>{l.sourceAssetType}: {l.sourceAssetRef}</> },
        { key: 'target', header: t('m41.lineage.target'), render: (l) => <>{l.targetAssetType}: {l.targetAssetRef}</> },
        { key: 'transformation', header: t('m41.lineage.transformation'), render: (l) => l.transformationType },
        { key: 'depth', header: t('m41.lineage.depth'), render: (l) => l.depth },
      ]}
    />
  );

  return (
    <main aria-label="data-lineage-viewer">
      <h1>{t('m41.lineage.title')}</h1>

      <section aria-label="lineage-record">
        <h2>{t('m41.lineage.record')}</h2>
        <form aria-label="record-form" onSubmit={(e) => void record(e)}>
          <label>{t('m41.common.datasetId')}<input aria-label="field-dataset-id" value={edgeDatasetId} onChange={(e) => setEdgeDatasetId(e.target.value)} /></label>
          <label>{t('m41.lineage.sourceType')}
            <select aria-label="field-source-type" value={sourceType} onChange={(e) => setSourceType(e.target.value)}>
              {ASSET_TYPES.map((ty) => <option key={ty} value={ty}>{ty}</option>)}
            </select>
          </label>
          <label>{t('m41.common.sourceRef')}<input aria-label="field-source-ref" value={sourceRef} onChange={(e) => setSourceRef(e.target.value)} /></label>
          <label>{t('m41.lineage.targetType')}
            <select aria-label="field-target-type" value={targetType} onChange={(e) => setTargetType(e.target.value)}>
              {ASSET_TYPES.map((ty) => <option key={ty} value={ty}>{ty}</option>)}
            </select>
          </label>
          <label>{t('m41.lineage.targetRef')}<input aria-label="field-target-ref" value={targetRef} onChange={(e) => setTargetRef(e.target.value)} /></label>
          <label>{t('m41.lineage.transformation')}
            <select aria-label="field-transformation" value={transformationType} onChange={(e) => setTransformationType(e.target.value)}>
              {TRANSFORMATIONS.map((ty) => <option key={ty} value={ty}>{ty}</option>)}
            </select>
          </label>
          <label>{t('m41.lineage.direction')}
            <select aria-label="field-direction" value={direction} onChange={(e) => setDirection(e.target.value)}>
              {DIRECTIONS.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </label>
          <Button variant="primary" type="submit" aria-label="submit-record">{t('m41.lineage.recordBtn')}</Button>
        </form>
        {actionError && <p role="alert" aria-label="lineage-action-error">{actionError}</p>}
        {notice && <p aria-label="lineage-notice">{notice}</p>}
      </section>

      <section aria-label="lineage-lookup">
        <h2>{t('m41.lineage.datasetLineage')}</h2>
        <form aria-label="lookup-form" onSubmit={lookup}>
          <label>{t('m41.common.datasetId')}<input aria-label="field-lookup-dataset-id" value={datasetId} onChange={(e) => setDatasetId(e.target.value)} /></label>
          <Button variant="primary" type="submit" aria-label="submit-lookup">{t('m41.lineage.viewLineage')}</Button>
        </form>
      </section>

      {error && <p role="alert" aria-label="lineage-error">{error}</p>}
      {loading ? <p>{t('common.loading')}</p> : lineage && (
        <>
          <section aria-label="lineage-upstream">
            <h2>{t('m41.lineage.upstream')}</h2>
            {renderEdges('upstream', lineage.upstream)}
          </section>
          <section aria-label="lineage-downstream">
            <h2>{t('m41.lineage.downstream')}</h2>
            {renderEdges('downstream', lineage.downstream)}
          </section>
        </>
      )}
    </main>
  );
}
